import { GmgStyleProvider, InfoModalProps, InputModalProps, ModalType, ThemeType } from '@gmg/gmg-react-components';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import i18next from 'i18next';
import { FunctionComponent, cloneElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { signOutWithEntra } from 'src/auth/entraLogout';
import { Language as APILanguage } from 'src/graphql/generated/types';
import { useSetApplicationTheme } from 'src/graphql/useSetApplicationTheme';
import { useThemeApplicationSetting } from 'src/graphql/useThemeApplicationSetting';
import { mapFromApiLanguage, useUpdateUserSettings } from 'src/graphql/useUpdateUserSettings';
import { mapToCurrentUser } from 'src/jwt/mappers/user';
import AppContext, { IAppContext, Language, defaultContext } from '../AppContext';
import { IConfig } from '../Config';
import { TrackingEvent, checkDisableTracking, getChangeLanguageEvent, getChangeThemeEvent } from '../tracking';
import App, { AppProps } from './App';
import LoadingPage from './LoadingPage';
import ErrorBoundary from './errorHandling/ErrorBoundary';
import { ApplicationError } from './errorHandling/ErrorMsg';

export interface AppContainerProps {
    config: IConfig;
    trackEvent: (event: TrackingEvent) => void;
};

const AppContainer: FunctionComponent<AppContainerProps> = props => {
    const [modal, setModal] = useState<ModalType>(undefined);
    const [errorType, setErrorType] = useState<undefined | ApplicationError>(undefined);
    const [theme, setTheme] = useState<ThemeType>('light');
    const { t } = useTranslation();

    const [appContext, setAppContext] = useState<IAppContext>({ // why state? reason for lifting context to state see https://reactjs.org/docs/context.html#caveats
        ...defaultContext,
        onError: (e: any) => {
            setErrorType(e.errors && e.errors.length && e.errors[0].errorType === 'Unauthorized'
                ? 'unauthorized'
                : 'unhandled',
            );
        },
        onShowModal: (variant: 'infoModal' | 'inputModal', options: InfoModalProps | InputModalProps) => {
            const onClose = () => setModal(undefined);
            setModal({
                variant,
                options: {
                    ...options,
                    content: cloneElement(options.content, { ...options.content.props, onClose }),
                    onClose,
                    btnCancelCaption: t('GenericModal.btn_cancel_caption', 'Cancel'),
                },
            });
        },
        featureFlags: props.config.featureFlags,
        partnerPortalBaseUrl: props.config.partnerPortalBaseUrl,
        partnerPortalIdentityProviderName: props.config.partnerPortalIdentityProviderName,
        partnerPortalUserPoolWebClientId: props.config.partnerPortalUserPoolWebClientId,
    });

    // required for AWS sign in page
    // Redirects the user to the redirect URL after sign in.
    // The hosted UI does the redirect not automatically.
    // This method is recommended by AWS.
    Hub.listen('auth', ({ payload }) => {
        // "signInWithRedirect" | "signInWithRedirect_failure" | "tokenRefresh" | "tokenRefresh_failure" | "customOAuthState" | "signedIn" | "signedOut"
        switch (payload.event) {
            case 'customOAuthState':
                const data = JSON.parse(payload.data);

                if (data.redirectTo && window.location.href !== data.redirectTo) {
                    window.location.href = data.redirectTo;
                }
                break;
        }
    });

    // Settings Api
    const { data: themeSetting, isFetching: isFetchingApplicationSettings } = useThemeApplicationSetting();

    const themeSettingMutation = useSetApplicationTheme();
    const userSettingsMutation = useUpdateUserSettings();

    useEffect(() => {
        if (!themeSetting) return;
        setTheme(themeSetting);
    }, [themeSetting]);

    // required for AWS sign in page
    const handleSignIn = async () => {

        const tokens = (await fetchAuthSession()).tokens;
        const idTokenPayload = tokens?.idToken?.payload!;

        setAppContext((ctx): IAppContext => ({
            ...ctx,
            user: mapToCurrentUser(idTokenPayload, props.config.modules),
            trackEvent: props.trackEvent,
        }));

        checkDisableTracking(idTokenPayload.email as string, props.config.tracking.ignore);

        const localeUserSettings: APILanguage = idTokenPayload.locale as APILanguage || APILanguage.En;
        setLanguage(mapFromApiLanguage(localeUserSettings));
    };

    useEffect(() => {
        void (async () => {
            try {
                // getCurrentUser throws an exception if the user is not signed in.
                await getCurrentUser();
                await handleSignIn();
            } catch { }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChangeTheme = (themeType: ThemeType) => {
        appContext.trackEvent(getChangeThemeEvent(themeType));
        setTheme(themeType);
        // persist theme in Settings Service
        themeSettingMutation.mutate(themeType); // TODO: check why ts check does not work
    };

    const setLanguage = (language: Language) => {
        void i18next.changeLanguage(language);
        setAppContext((ctx): IAppContext => ({ ...ctx, language }));
    };

    const handleChangeLanguage = async (language: Language) => {
        appContext.trackEvent(getChangeLanguageEvent(language));
        setLanguage(language);
        // await is required. Without, refreshing the token will be executed before the mutation.
        const wasUpdateLanguageSuccessful = await userSettingsMutation.mutateAsync(language);
        // Refresh user token because "locale" attribute did change.
        // If we don't do that, the language will be reset on the next page refresh.
        if (wasUpdateLanguageSuccessful) {
            await fetchAuthSession({ forceRefresh: true });
        }
    };

    const handleUserUpdateAfterRefreshingToken = async () => {
        const session = await fetchAuthSession({ forceRefresh: true });
        setAppContext((ctx): IAppContext => ({
            ...ctx,
            user: mapToCurrentUser(session.tokens?.idToken?.payload!, props.config.modules),
        }));
    };

    const handleSignOut = async () => {
        try {
            await signOutWithEntra(props.config);
        } catch (error) {
            appContext.onError(error instanceof Error ? error : new Error(String(error)));
        }
    };

    const userPoolParams = {
        clientId: props.config.amplify.Auth!.Cognito.userPoolClientId!,
        endpoint: props.config.amplify.Auth!.Cognito.loginWith!.oauth!.domain!,
    };

    const appProps: AppProps = {
        modal,
        signOut: handleSignOut,
        theme,
        onChangeTheme: handleChangeTheme,
        onChangeLanguage: handleChangeLanguage,
        errorType,
        env: props.config.env,
        handleUserUpdateAfterRefreshingToken,
        userPoolParams,
        apiEndpoint: props.config.amplify.API!.GraphQL!.endpoint,
    };

    return (
        <AppContext.Provider value={appContext}>
            <ErrorBoundary>
                <GmgStyleProvider theme={theme}>
                    {
                        isFetchingApplicationSettings
                            ? <LoadingPage />
                            : <App {...appProps} />
                    }

                </GmgStyleProvider>
            </ErrorBoundary>
        </AppContext.Provider>
    );
};

export default AppContainer;